import React from 'react';
import { EDM_UI } from '../../../../utils/edm';
import type { EdmMapping, EdmMappingTarget } from '../../../../hooks/useEdmImport';

export const MAPPING_TARGETS: Array<{ value: EdmMappingTarget; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'phone', label: 'Phone' },
  { value: 'tags', label: 'Tags' },
  { value: 'status', label: 'Status/Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'broker', label: 'Broker (email)' },
  { value: 'ignore', label: 'Ignore' },
];

const REPEATABLE = new Set<EdmMappingTarget>(['tags', 'ignore']);
const SIGNAL_TARGETS = new Set<EdmMappingTarget>(['status', 'unsubscribed', 'bounced', 'complaint']);

/** First validation error for a mapping, or null when it can be confirmed. */
export function mappingError(mapping: EdmMapping): string | null {
  const targets = Object.values(mapping);
  if (targets.filter((t) => t === 'email').length !== 1) return 'Choose which column holds the email address.';
  const seen = new Set<EdmMappingTarget>();
  for (const t of targets) {
    if (REPEATABLE.has(t)) continue;
    if (seen.has(t)) {
      const label = MAPPING_TARGETS.find((m) => m.value === t)?.label ?? t;
      return `${label} is chosen for more than one column. Pick one.`;
    }
    seen.add(t);
  }
  return null;
}

function samples(rows: Array<Record<string, string>>, column: string): string {
  const out: string[] = [];
  for (const r of rows) {
    const v = (r[column] ?? '').trim();
    if (v) out.push(v.length > 40 ? `${v.slice(0, 40)}…` : v);
    if (out.length === 3) break;
  }
  return out.join(', ');
}

const MappingTable: React.FC<{
  columns: string[];
  sampleRows: Array<Record<string, string>>;
  mapping: EdmMapping;
  suggested: EdmMapping;
  onChange: (m: EdmMapping) => void;
}> = ({ columns, sampleRows, mapping, suggested, onChange }) => {
  const colFor = (target: EdmMappingTarget) => columns.filter((c) => mapping[c] === target);
  const cell = (row: Record<string, string>, target: EdmMappingTarget) =>
    colFor(target)
      .map((c) => row[c] ?? '')
      .filter(Boolean)
      .join(' ');
  const signal = (row: Record<string, string>) =>
    columns
      .filter((c) => SIGNAL_TARGETS.has(mapping[c]))
      .map((c) => (row[c] ?? '').trim())
      .filter(Boolean)
      .join(', ');

  return (
    <div className="space-y-6">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
            <th className="py-2 pr-4">Column in your file</th>
            <th className="py-2 pr-4">Sample values</th>
            <th className="py-2">Maps to</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((c) => {
            const id = `edm-map-${c}`;
            const auto = suggested[c] !== undefined && suggested[c] === mapping[c] && mapping[c] !== 'ignore';
            return (
              <tr key={c} className="border-b border-gray-800">
                <td className="py-2 pr-4 text-gray-200">
                  <label htmlFor={id}>{c}</label>
                </td>
                <td className="py-2 pr-4 font-mono text-xs text-gray-400">{samples(sampleRows, c)}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <select
                      id={id}
                      value={mapping[c] ?? 'ignore'}
                      onChange={(e) => onChange({ ...mapping, [c]: e.target.value as EdmMappingTarget })}
                      className={EDM_UI.input}
                    >
                      {MAPPING_TARGETS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    {auto && <span className="text-xs text-blue-300">auto</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-200">Sample rows</h3>
        <div className="max-h-[24rem] overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">First</th>
                <th className="py-2 pr-4">Last</th>
                <th className="py-2 pr-4">Phone</th>
                <th className="py-2 pr-4">Broker</th>
                <th className="py-2">Signal</th>
              </tr>
            </thead>
            <tbody>
              {sampleRows.map((row, i) => (
                <tr key={i} className="border-b border-gray-800 text-gray-300">
                  <td className="py-1.5 pr-4">{cell(row, 'email')}</td>
                  <td className="py-1.5 pr-4">{cell(row, 'firstName')}</td>
                  <td className="py-1.5 pr-4">{cell(row, 'lastName')}</td>
                  <td className="py-1.5 pr-4">{cell(row, 'phone')}</td>
                  <td className="py-1.5 pr-4">{cell(row, 'broker')}</td>
                  <td className="py-1.5">{signal(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MappingTable;
